import PropTypes from 'prop-types';
import { noCase } from 'change-case';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Icon } from '@iconify/react';
import bellFill from '@iconify/icons-eva/bell-fill';
import clockFill from '@iconify/icons-eva/clock-fill';
import doneAllFill from '@iconify/icons-eva/done-all-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill'; // Import the close circle icon
import {
  Box,
  List,
  Badge,
  Button,
  Avatar,
  Tooltip,
  Divider,
  Typography,
  ListItemText,
  ListSubheader,
  ListItemAvatar,
  ListItemButton,
  Link,
  IconButton,
} from '@mui/material';

// Import alpha function here
import { alpha } from '@mui/material/styles';

import useAuth from '../../hooks/useAuth';
import {
  useDeleteNotificationMutation,
  useGetNotificationsQuery,
  useDeleteNotificationsMutation,
} from '../../redux/slices/api';

import mockData from '../../utils/mock-data';
import { fToNow } from '../../utils/formatTime';
import Scrollbar from '../../components/Scrollbar';
import MenuPopover from '../../components/MenuPopover';
import { MAvatar, MIconButton } from '../../components/@material-extend';
import createAvatar from '../../utils/createAvatar';

// ----------------------------------------------------------------------

const TITLES = [
  'Your order is placed',
  'Sylvan King',
  'You have new message',
  'You have new mail',
  'Delivery processing',
];

const DESCRIPTIONS = [
  'waiting for shipping',
  'answered to your comment on the Minimal',
  '5 unread messages',
  'sent from Guido Padberg',
  'Your order is being shipped',
];

const TYPES = ['order_placed', 'friend_interactive', 'chat_message', 'mail', 'order_shipped'];

const AVATARS = [null, mockData.image.avatar(2), null, null, null];

const UNREADS = [true, true, false, false, false];

const MOCK_NOTIFICATIONS = [...Array(5)].map((_, index) => ({
  id: mockData.id(index),
  title: TITLES[index],
  description: DESCRIPTIONS[index],
  avatar: AVATARS[index],
  type: TYPES[index],
  createdAt: mockData.time(index),
  isUnRead: UNREADS[index],
}));

// ----------------------------------------------------------------------

function renderContent(notification) {
  const title = (
    <Typography variant="subtitle2">
      {notification.title}
      <Typography
        component="span"
        variant="body2"
        sx={{ color: 'text.secondary' }}
        dangerouslySetInnerHTML={{ __html: notification.description }} // Use this to render HTML
      />
    </Typography>
  );

  return {
    displayName: notification.title,
    title,
  };
}

NotificationItem.propTypes = {
  notification: PropTypes.object.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function NotificationItem({ notification, onDelete }) {
  const { displayName, title } = renderContent(notification);
  const [isHovered, setIsHovered] = useState(false);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation(); // Prevent the click event from triggering navigation
    onDelete(notification.id); // Delete the notification
  };

  const handleNotificationClick = () => {
    onDelete(notification.id); // Delete the notification and then navigate
  };

  return (
    <Box
      sx={{
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        bgcolor: isHovered ? alpha('#ffffff', 0.2) : 'background.paper', // Change background color on hover
        '&:hover': {
          cursor: 'pointer',
        },
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <ListItemButton
        component={RouterLink}
        to={notification.url}
        onClick={handleNotificationClick} // Handle click on the entire item
        sx={{
          py: 1.5,
          px: 2.5,
          mt: '1px',
          flexGrow: 1, // Allow the button to take up the remaining space
          ...(notification.isUnRead && {
            bgcolor: 'action.selected',
          }),
        }}
      >
        <Box mr={2}>
          <MAvatar src="" alt={displayName} color={createAvatar(displayName).color}>
            {createAvatar(displayName).name}
          </MAvatar>
        </Box>
        <ListItemText
          primary={title}
          secondary={
            <>
              <Typography
                variant="caption"
                sx={{
                  mt: 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  color: 'text.disabled',
                }}
              >
                <Box component={Icon} icon={clockFill} sx={{ mr: 0.5, width: 16, height: 16 }} />
                {fToNow(notification.createdAt)}
              </Typography>
            </>
          }
        />
      </ListItemButton>
      {isHovered && (
        <IconButton
          sx={{
            position: 'absolute',
            right: 4,
            bottom: 1,
            color: 'rgba(128, 128, 128, 0.5)', // Light grey with some transparency
            bgcolor: 'rgba(245, 245, 245, 0.8)', // Background color of the circle
            borderRadius: '50%', // Ensure it stays circular
            width: 40, // Size consistent with the icon
            height: 40, // Size consistent with the icon
            '&:hover': {
              color: 'rgba(64, 64, 64, 0.8)', // Darker grey on hover
              bgcolor: 'rgba(230, 230, 230, 0.8)', // Slightly darker background on hover
            },
          }}
          onClick={handleDeleteClick} // Handle delete button click
        >
          <Icon icon={closeCircleFill} width={40} height={40} />
        </IconButton>
      )}
    </Box>
  );
}

export default function NotificationsPopover() {
  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const [showLength, setShowLength] = useState(5);
  const { user } = useAuth();
  const { data, isError } = useGetNotificationsQuery();
  const [deleteNotification, { isLoading: isLoadingDelete }] = useDeleteNotificationMutation();
  const [deleteNotifications, { isLoading: isLoadingDeleteAll }] = useDeleteNotificationsMutation();

  useEffect(() => {
    if (data && user.email) {
      setNotifications(
        data.map((notificationProfile, i) => {
          const { id, notification } = notificationProfile;

          let description;
          let url;

          switch (notification.type) {
            case 'new_sheet_revision':
              description = ` made new revision <strong>${notification.info.revision_name} for sheet <strong>${notification.info.sheet_name} in project <strong>${notification.info.project_name} `;
              url = `/dashboard/yawn/schedule/${notification.info.sheet_id}/edit`;
              break;
            case 'sheet_row_update':
              description = ` made new update for item <strong>${notification.info.item}</strong> for sheet <strong>${notification.info.sheet_name}</strong> in project <strong>${notification.info.project_name}</strong>`;
              url = `/dashboard/yawn/schedule/${notification.info.sheet_id}/edit`;
              break;
            case 'new_sheet_row_comment':
              description = ` made new schedule row commnent <strong>${notification.info.comment_body}</strong> for sheet <strong>${notification.info.sheet_name}</strong> in project <strong>${notification.info.project_name}</strong>`;
              url = `/dashboard/yawn/schedule/${notification.info.sheet_id}/edit`;
              break;
            case 'new_product':
              description = ` made new product <strong>${notification.info.product_name}</strong> of <strong>${notification.info.company_name}</strong>`;
              url = `/dashboard/yawn/product/${notification.info.id}/list`;
              break;

            case 'new_product_comment':
              description = ` made new comment for product <strong>${notification.info.product_name}</strong> of <strong>${notification.info.company_name}</strong>`;
              url = `/dashboard/yawn/product/${notification.info.product_id}/list`;
              break;

            case 'user_added_to_project':
              description = ` invited you into project <strong>${notification.info.project_name}</strong>`;
              url = `/dashboard/yawn/project/${notification.info.project_id}/edit`;
              break;
            case 'user_added_to_sheet':
              description = ` invited you into sheet <strong>${notification.info.sheet_name}</strong> in project <strong>${notification.info.project_name}</strong>`;
              url = `/dashboard/yawn/schedule/${notification.info.sheet_id}/edit`;
              break;
            case 'invitation_to_company':
              description = ` invited you into company <strong>${notification.info.company}</strong>`;
              url = `/dashboard/yawn/invitation/${notification.info.id}/`;
              break;
            case 'new_spec_section_discussion':
              description = ` suggested changes to specification <strong>${notification.info.spec_name}</strong> in section <strong>${notification.info.spec_section_name}</strong>`;
              url = `/dashboard/yawn/spec/${notification.info.spec_section_id}/discussion/${notification.info.id}`;
              break;
            default:
              break;
          }

          return {
            id,
            title: notification?.info?.created_by_name || notification?.info?.created_by,
            description,
            type: notification.type,
            createdAt: notification.created_at,
            url,
            isUnRead: true,
          };
        })
      );
    }
  }, [data]);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleView = () => {
    setShowLength(showLength === 5 ? notifications.length : 5);
  };

  const handleDeleteItem = (id) => {
    deleteNotification({ id });
  };

  const handleMarkAllAsRead = () => {
    const idsArray = notifications.map((notification) => notification.id);
    deleteNotifications({ ids: idsArray });
  };

  return (
    <>
      <MIconButton
        ref={anchorRef}
        size="extraLarge"
        color={open ? 'primary' : 'default'}
        onClick={handleOpen}
        sx={{
          ...(open && {
            bgcolor: (theme) =>
              alpha(theme.palette.primary.main, theme.palette.action.focusOpacity),
          }),
        }}
      >
        <Badge badgeContent={notifications.length} color="error">
          <Icon icon={bellFill} width={20} height={20} />
        </Badge>
      </MIconButton>

      <MenuPopover
        open={open}
        onClose={handleClose}
        anchorEl={anchorRef.current}
        sx={{ width: 360 }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', py: 2, px: 2.5 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="subtitle1">Notifications</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
              You have {notifications.length} unread messages
            </Typography>
          </Box>

          {notifications.length > 0 && (
            <Tooltip title=" Mark all as read">
              <MIconButton color="primary" onClick={handleMarkAllAsRead}>
                <Icon icon={doneAllFill} width={20} height={20} />
              </MIconButton>
            </Tooltip>
          )}
        </Box>

        <Divider />

        <Scrollbar sx={{ height: { xs: 340, sm: '40vh' } }}>
          <List disablePadding>
            {notifications
              .slice(0, showLength)
              .map((notification, index) => (
                <NotificationItem
                  key={index}
                  notification={notification}
                  onDelete={() => handleDeleteItem(notification.id)}
                />
              ))}
          </List>
        </Scrollbar>

        <Divider />

        <Box sx={{ p: 1 }}>
          <Button fullWidth disableRipple onClick={handleView}>
            {showLength === 5 ? 'View All' : 'View Less'}
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
